"use client";

import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
} from "@headlessui/react";
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  UserIcon,
  ArchiveBoxIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../services/api";
import ThemeToggle from "./ThemeToggle";
import NavVisibilityToggle from "./NavVisibilityToggle";
import { useNav } from "../context/NavContext";
import UserAvatar from "./UserAvatar";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const navItemClass = (active) =>
  classNames(
    active
      ? "border-sky-400/70 bg-gradient-to-r from-sky-500/30 via-blue-700/25 to-transparent text-sky-950 shadow-[0_0_24px_rgba(56,189,248,0.35)] dark:text-white"
      : "border-transparent text-gray-700 hover:border-sky-400/40 hover:bg-sky-500/10 hover:text-sky-700 dark:text-gray-300 dark:hover:text-white",
    "group flex gap-x-3 rounded-xl border px-3 py-2.5 text-sm/6 font-semibold transition duration-200"
  );

const navIconClass = (active) =>
  classNames(
    active ? "text-sky-500 dark:text-sky-300" : "text-gray-400 group-hover:text-sky-600 dark:group-hover:text-sky-300",
    "size-6 shrink-0 transition duration-200"
  );

export default function NavBar() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const { navMode } = useNav();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await api.get("/users/me");
        setUser(res.data);
      } catch (_err) {
        setUser(null);
      }
    };

    fetchUser();
  }, []);

  const navigation = [
    { name: "Accueil", href: "/", icon: HomeIcon },
    { name: "Stockage", href: "/stockage", icon: ArchiveBoxIcon },
    { name: "Profil", href: "/profile", icon: UserIcon },
    ...(user?.GradeID === 1 || user?.GradeID === 2
      ? [{ name: "Administration", href: "/administration", icon: ShieldCheckIcon }]
      : []),
  ];

  const dropdownItems = [
    { name: "Votre profil", href: "/profile" },
    { name: "Paramètres", href: "/settings" },
    ...(user?.GradeID === 1 || user?.GradeID === 2 ? [{ name: "Administration", href: "/administration" }] : []),
  ];

  const navVisibilityClass =
    navMode === "hover"
      ? "opacity-0 group-hover:opacity-100 transition-all duration-300 lg:-translate-x-64 group-hover:translate-x-0"
      : "opacity-100 translate-x-0 transition-all duration-300";

  const topbarPaddingClass =
    navMode === "hover"
      ? "lg:pl-0 group-hover:lg:pl-72 opacity-0 group-hover:opacity-100 transition-all duration-300"
      : "lg:pl-72 opacity-100 transition-all duration-300";

  const handleLogout = async () => {
    const token = localStorage.getItem("token");
    try {
      if (token) {
        await fetch("/api/users/logout", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (err) {
      console.error("Erreur lors de la déconnexion :", err);
    } finally {
      localStorage.removeItem("token");
      setUser(null);
      navigate("/login");
    }
  };

  const renderNav = () => (
    <nav className="flex flex-1 flex-col">
      <ul className="flex flex-1 flex-col gap-y-7">
        <li>
          <ul className="-mx-2 space-y-1">
            {navigation.map((item) => {
              const active = location.pathname === item.href;
              return (
                <li key={item.name}>
                  <a href={item.href} className={navItemClass(active)}>
                    <item.icon aria-hidden="true" className={navIconClass(active)} />
                    {item.name}
                  </a>
                </li>
              );
            })}
          </ul>
        </li>
        <li className="mt-auto">
          <a href="/settings" className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-gray-700 hover:bg-gray-50 hover:text-sky-600 dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white">
            <Cog6ToothIcon aria-hidden="true" className="size-6 shrink-0 text-gray-400 group-hover:text-sky-600 dark:group-hover:text-white" />
            Paramètres
          </a>
        </li>
      </ul>
    </nav>
  );

  return (
    <>
      <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
        <DialogBackdrop transition className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0" />
        <div className="fixed inset-0 flex">
          <DialogPanel transition className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full">
            <TransitionChild>
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                  <span className="sr-only">Fermer la navigation</span>
                  <XMarkIcon aria-hidden="true" className="size-6 text-white" />
                </button>
              </div>
            </TransitionChild>
            <div className="relative flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4 dark:bg-gray-900">
              <div className="relative flex h-16 shrink-0 items-center">
                <a href="/" className="flex items-center gap-3">
                  <img src="/logo.png" alt="Logo" className="h-8 w-auto" />
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">Stockage</span>
                </a>
              </div>
              {renderNav()}
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      <div className="group">
        <div className={classNames("hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col backdrop-blur-2xl", navVisibilityClass)}>
          <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4 dark:border-white/10 dark:bg-black/50">
            <div className="flex h-16 shrink-0 items-center">
              <a href="/" className="flex items-center gap-3">
                <img src="/logo.png" alt="Logo" className="h-8 w-auto" />
                <span className="text-sm font-semibold text-gray-900 dark:text-white">Stockage</span>
              </a>
            </div>
            {renderNav()}
          </div>
        </div>

        <div className={classNames("sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white/80 px-4 shadow-sm backdrop-blur dark:border-white/10 dark:bg-gray-900/80 sm:gap-x-6 sm:px-6 lg:px-8", topbarPaddingClass)}>
          <button type="button" onClick={() => setSidebarOpen(true)} className="-m-2.5 p-2.5 text-gray-700 lg:hidden dark:text-gray-300">
            <span className="sr-only">Ouvrir la navigation</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>

          <div className="h-6 w-px bg-gray-200 lg:hidden dark:bg-white/10" aria-hidden="true" />
          <div className="flex flex-1 items-center justify-end gap-x-4 self-stretch lg:gap-x-6">
            <ThemeToggle />
            <NavVisibilityToggle />
            <Menu as="div" className="relative">
              <MenuButton className="-m-1.5 flex items-center p-1.5">
                <span className="sr-only">Ouvrir le menu utilisateur</span>
                <UserAvatar user={user} className="size-8 rounded-full bg-gray-50" />
                <span className="hidden lg:flex lg:items-center">
                  <span aria-hidden="true" className="ml-4 text-sm/6 font-semibold text-gray-900 dark:text-white">
                    {user?.Surnom || "Compte"}
                  </span>
                  <ChevronDownIcon aria-hidden="true" className="ml-2 size-5 text-gray-400" />
                </span>
              </MenuButton>
              <MenuItems transition className="absolute right-0 z-10 mt-2.5 w-44 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-none dark:bg-gray-800 dark:ring-white/10">
                {dropdownItems.map((item) => (
                  <MenuItem key={item.name}>
                    <a href={item.href} className="block px-3 py-1 text-sm/6 text-gray-900 data-[focus]:bg-gray-50 dark:text-white dark:data-[focus]:bg-white/5">
                      {item.name}
                    </a>
                  </MenuItem>
                ))}
                <MenuItem>
                  <button type="button" onClick={handleLogout} className="block w-full px-3 py-1 text-left text-sm/6 text-gray-900 data-[focus]:bg-gray-50 dark:text-white dark:data-[focus]:bg-white/5">
                    Déconnexion
                  </button>
                </MenuItem>
              </MenuItems>
            </Menu>
          </div>
        </div>
      </div>
    </>
  );
}
